<template>
  <v-col
    v-if="visible && show"
    cols="12"
    :md="size('md', type !== 'description' ? 8 : 12)"
    :lg="size('lg', type !== 'description' ? 8 : 12)"
    :xl="size('xl', type !== 'description' ? 8 : 12)"
    v-show="type !== 'hidden' && config.isHidden !== 'true'"
  >
    <div class="component-field">
      <component
        style="width: 100%;"
        v-if="rendered"
        v-bind:is="component"
        v-bind="fieldData"
        :inputData.sync="fieldValue">
        <template v-slot:required v-if="fieldRequired">
          <v-icon class="required-icon">mdi-asterisk</v-icon>
        </template>
      </component>
       <v-menu v-if="config && config.tooltip">
        <template v-slot:activator="{ on: menu, attrs }">
          <v-tooltip right>
            <template v-slot:activator="{ on }">
              <v-icon
                style="margin-left: 0.5rem;"
                small
                v-bind="attrs"
                v-on="on"
              >
                mdi-information
              </v-icon>
            </template>
            <span>{{config && config.tooltip ? config.tooltip : ''}}</span>
          </v-tooltip>
        </template>
      </v-menu>
    </div>
  </v-col>
</template>

<script>
export const FIELD_TYPES = {
  date: 'DatetimeField',
  datetime: 'DatetimeField',
  datetimeapprovals: 'DatetimeApprovalsField',
  datetimefrom: 'DatetimeFrom',
  datetimeuntil: 'DatetimeUntil',
  description: 'DescriptionField',
  file: 'FileField',
  number: 'NumberField',
  radio: 'RadioField',
  search: 'SearchField',
  select: 'SelectField',
  switch: 'SwitchField',
  textarea: 'TextareaField',
  text: 'TextField',
  email: 'TextField',
  emailsas: 'TextField',
  tel: 'TextField',
  textroom: 'TextField',
  mobile: 'TextField',
  calendar: 'DatetimeField',
  group: 'GroupField',
  hidden: 'HiddenField',
  workflow: 'WorkflowField',
  showMediaLink: 'ShowMediaLink',
  feedback: 'Feedback',
};

export const EXTENDED_FIELD_TYPES = {
  acudecalendar: ['calendar', 'AcudeCalendarField'], // 0: FieldType, 1: TargetComponent
  contactsearch: ['search', 'ContactSearchField'],
  contactsearchmultiple: ['search', 'ContactSearchMultipleField'],
  suscriptorsearch: ['search', 'SuscriptorSearchField'],
  altirissearch: ['search', 'AltirisSearchField'],
  catalogsearch: ['search', 'CatalogSearchField'],
  barcodesearch: ['search', 'BarCodeScannerField'],
  tablaMaestraSelect: ['select', 'TablaMaestraSelectField'],
  tablaMaestraSearch: ['search', 'TablaMaestraSearchField'],
  remotedescription: ['description', 'RemoteDescriptionField'],
  workplace: ['search', 'WorkplaceField'],
};

const GENERIC_RULES = {
  emailsas: [
    function emailsasValidation(value) {
      return (value == null || value === '' || /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@juntadeandalucia\.es$/.test(value)) || this.$t('validation.emailSAS');
    },
  ],
  email: [
    function emailValidation(value) {
      return (value == null || value === '' || /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(value)) || this.$t('validation.email');
    },
  ],
  tel: [
    function telValidation(value) {
      return (value == null || value === '' || /^([0-9]{6}|[0-9]{9})*(\/([0-9]{6}|[0-9]{9}))*$/.test(value)) || this.$t('validation.tel');
    },
  ],
  textroom: [
    function textRoomValidation(value) {
      return (value == null || value === '' || value.length <= 30) || this.$t('validation.textRoom');
    },
  ],
  mobile: [
    function mobileValidation(value) {
      return (value == null || value === '' || /^((6|7)[ -]*([0-9][ -]*){8})*$/.test(value)) || this.$t('validation.mobile');
    },
  ],
  file: [
    function sizeValidation(value) {
      return !value.some((file) => file.size > 20000000) || this.$t('validation.file.size');
    },
    function lengthValidation(value) {
      return !((value || []).length > 8) || this.$t('validation.file.length');
    },
  ],
};

export default {
  watch: {
    fieldValue(val) {
      if (this.eventBus != null) {
        this.eventBus.emit(`change:${this.alias}`, val);

        if (this.type === 'hidden') {
          const disableSubmit = (this.fieldValue.value === 'true' || this.fieldValue.value === true)
            && this.config.disableSubmit === 'true';
          this.eventBus.emit('form:disableSubmit', disableSubmit);
        }
      }
      const { value } = val;

      const data = {
        alias: this.alias,
        value,
      };
      this.eventBus.emit('form:change', data);
    },
  },
  props: {
    eventBus: Object,
    expressionInterpreter: {
      type: Object,
      required: true,
    },
    alias: {
      type: String,
      required: true,
    },
    type: {
      type: String,
      required: true,
      validator: (name) => typeof FIELD_TYPES[name] === 'string',
    },
    label: String,
    required: Boolean,
    disabled: Boolean,
    config: {
      type: Object,
      default: () => ({}),
    },
    dependency: {
      type: Object,
      default: () => ({}),
    },
    params: {
      type: Object,
      default: () => ({}),
    },
    specialValue: {
      type: Object,
      default: () => ({}),
    },
    sections: {
      type: Array,
      default: () => ([]),
    },
    changeOptions: Function,
    disableOutlined: Boolean,
    prependIcon: Boolean,
  },
  data() {
    return {
      fieldValue: { },
      show: true,
      rendered: true,
    };
  },
  components: {
    DatetimeField: () => import(/* webpackChunkName: "formfields" */ '@/components/form/field-types/Datetime.vue'),
    DatetimeApprovalsField: () => import(/* webpackChunkName: "formfields" */ '@/components/form/field-types/DatetimeApprovals.vue'),
    DatetimeFrom: () => import(/* webpackChunkName: "formfields" */ '@/components/form/field-types/DatetimeFrom.vue'),
    DescriptionField: () => import(/* webpackChunkName: "formfields" */ '@/components/form/field-types/Description.vue'),
    FileField: () => import(/* webpackChunkName: "formfields" */ '@/components/form/field-types/File.vue'),
    NumberField: () => import(/* webpackChunkName: "formfields" */ '@/components/form/field-types/Number.vue'),
    RadioField: () => import(/* webpackChunkName: "formfields" */ '@/components/form/field-types/Radio.vue'),
    SearchField: () => import(/* webpackChunkName: "formfields" */ '@/components/form/field-types/Search.vue'),
    SelectField: () => import(/* webpackChunkName: "formfields" */ '@/components/form/field-types/Select.vue'),
    SwitchField: () => import(/* webpackChunkName: "formfields" */ '@/components/form/field-types/Switch.vue'),
    TextareaField: () => import(/* webpackChunkName: "formfields" */ '@/components/form/field-types/Textarea.vue'),
    TextField: () => import(/* webpackChunkName: "formfields" */ '@/components/form/field-types/Text.vue'),
    GroupField: () => import(/* webpackChunkName: "formfields" */ '@/components/form/field-types/Group.vue'),
    HiddenField: () => import(/* webpackChunkName: "formfields" */ '@/components/form/field-types/Hidden.vue'),
    WorkflowField: () => import(/* webpackChunkName: "formfields" */ '@/components/form/field-types/Workflow.vue'),
    ShowMediaLink: () => import(/* webpackChunkName: "formfields" */ '@/components/form/field-types/ShowMediaLink.vue'),
    Feedback: () => import(/* webpackChunkName: "formfields" */ '@/components/form/field-types/Feedback.vue'),

    // extended field types
    AcudeCalendarField: () => import(/* webpackChunkName: "formfields" */ '@/components/form/extended-types/AcudeCalendar.vue'),
    ContactSearchField: () => import(/* webpackChunkName: "formfields" */ '@/components/form/extended-types/ContactSearch.vue'),
    ContactSearchMultipleField: () => import(/* webpackChunkName: "formfields" */ '@/components/form/extended-types/ContactSearchMultiple.vue'),
    SuscriptorSearchField: () => import(/* webpackChunkName: "formfields" */ '@/components/form/extended-types/SuscriptorSearch.vue'),
    AltirisSearchField: () => import(/* webpackChunkName: "formfields" */ '@/components/form/extended-types/AltirisSearch.vue'),
    CatalogSearchField: () => import(/* webpackChunkName: "formfields" */ '@/components/form/extended-types/CatalogSearch.vue'),
    BarCodeScannerField: () => import(/* webpackChunkName: "formfields" */ '@/components/form/extended-types/BarCodeScanner.vue'),
    TablaMaestraSelectField: () => import(/* webpackChunkName: "formfields" */ '@/components/form/extended-types/TablaMaestraSelect.vue'),
    TablaMaestraSearchField: () => import(/* webpackChunkName: "formfields" */ '@/components/form/extended-types/TablaMaestraSearch.vue'),
    RemoteDescriptionField: () => import(/* webpackChunkName: "formfields" */ '@/components/form/extended-types/RemoteDescription.vue'),
    WorkplaceField: () => import(/* webpackChunkName: "formfields" */ '@/components/form/extended-types/Workplace.vue'),
  },
  computed: {
    component() {
      let component = FIELD_TYPES[this.type];
      if (typeof this.config.component === 'string' && EXTENDED_FIELD_TYPES[this.config.component]) {
        const [extendedFieldType, extendedComponent] = EXTENDED_FIELD_TYPES[this.config.component];
        if (extendedFieldType === this.type) {
          component = extendedComponent;
        }
      }
      return component;
    },
    isGroupField() {
      return this.type === 'group';
    },
    fieldData() {
      const params = Object.entries(this.expressionInterpreter.get('params'))
        .reduce((root, curr) => {
          const [key, value] = curr;
          // eslint-disable-next-line no-param-reassign
          root[key] = typeof value === 'function' ? (ctx) => value({ ...this.expressionInterpreter.ctx, ...ctx }) : value;
          return root;
        }, {});
      const rules = this.fieldRules.concat(this.fieldErrors);
      return {
        label: this.config.emptylabel !== 'true' ? this.expressionInterpreter.eval('config.label', this.label) : '',
        initialValue: this.expressionInterpreter.eval('config.initialvalue'),
        messageEmpty: this.config.messageempty,
        hint: this.config.hint,
        rules,
        params,
        required: this.fieldRequired,
        disabled: this.fieldDisabled,
        parentInterpreter: this.isGroupField ? this.expressionInterpreter : undefined,
        specialValue: this.specialValue ? this.specialValue : undefined,
        sections: this.sections ? this.sections : [],
        changeOptions: this.changeOptions,
        infoTooltip: this.config.tooltip ?? null,
        disableOutlined: this.disableOutlined ?? false,
        prependIcon: this.prependIcon ?? false,
      };
    },
    size() {
      return (tam, defValue) => {
        // groups default value will be 12
        const defaultValue = this.isGroupField ? 12 : defValue;
        return (this.config.size || {})[tam] || defaultValue;
      };
    },
    visible() {
      let visible = true;

      if (this.dependency.visibility) {
        visible = visible && this.expressionInterpreter.eval('dependency.visibility', false, this.fieldValue);
      }

      return visible;
    },
    fieldDisabled() {
      let { disabled } = this;
      // by default disabled will be considered an string
      // optionally we could configure this key as expr
      const disabledKey = Object.keys(this.config).find((k) => (k === 'disabled') || (k.startsWith('disabled') && k.includes(':')));
      if (disabledKey != null) {
        const evaluated = this.expressionInterpreter.eval('config.disabled', 'false', this.fieldValue) === 'true';
        disabled = disabled || evaluated;
      }
      return disabled;
    },
    fieldRequired() {
      let { required } = this;
      if (this.dependency.required) {
        required = required || Object.keys(this.dependency.required)
          .reduce((acum, current) => acum && this.expressionInterpreter.eval(`dependency.required.${current}`, false, this.expressionInterpreter.fields[current]), true);
      }
      return required;
    },
    fieldRules() {
      const validations = [];
      if (this.fieldRequired) {
        if (this.type === 'file') {
          validations.push((value) => (value != null && value.length > 0) || this.$t('validation.file.required'));
        } else if (this.params.multiple) {
          validations.push((value) => (value != null && value !== '' && typeof value !== 'undefined' && value.length > 0) || this.$t('validation.required'));
        } else {
          validations.push((value) => (value != null && value !== '' && typeof value !== 'undefined') || this.$t('validation.required'));
        }
      }

      // if (GENERIC_RULES[this.type] != null) {
      //   GENERIC_RULES[this.type].forEach((rule) => validations.push(rule.bind(this)));
      // }

      if (GENERIC_RULES[this.type] != null) {
        GENERIC_RULES[this.type].forEach((rule) => {
          validations.push((value) => {
            if (this.params?.isObfuscate && value != null && /\*/.test(value)) {
              const realValue = this.fieldValue?.value ?? value;
              return rule.bind(this)(realValue);
            }
            return rule.bind(this)(value);
          });
        });
      }

      return validations;
    },
    /**
     * custom validation errors
     * this has been excluded from field rules because they are not reactive
     * and no context change won't update the validity
     */
    fieldErrors() {
      const errors = [];

      if (this.config.validation) {
        Object.keys(this.config.validation)
          .filter((validation) => {
            const { item, value } = this.expressionInterpreter.ctx[this.alias] || {};
            if (value != null) {
              try {
                return !(this.expressionInterpreter.eval(`config.validation.${validation}`, null, { item, value }));
              } catch (ex) {
                console.error('error evaluating field error', ex);
              }
            }
            return false;
          })
          .forEach((validation) => {
            errors.push(this.config.errors[validation] || this.$t(`validation.errors.${validation}`));
          });
      }

      return errors;
    },
  },
  methods: {
    isVisible(is) {
      this.show = is;
    },
  },
  created() {
    if (Object.keys(this.config).length) {
      this.expressionInterpreter.load('config', this.config, [
        'validation',
        'readonly',
        'initialvalue',
        'isHidden',
        'disableSubmit',
        'cameraEnabled',
      ]);
      // this.fieldValue.value = this.expressionInterpreter.eval('config.initialvalue');
    }
    if (Object.keys(this.dependency).length) {
      this.expressionInterpreter.load('dependency', this.dependency, [
        'visibility',
        'required',
      ]);
    }
    if (Object.keys(this.params)) {
      this.expressionInterpreter.load('params', this.params, [], [
        'query',
        'lookandfeel',
        'filters',
        'apicges',
        'extraFilter',
      ]);
    }

    if (this.eventBus != null && typeof this.dependency.update === 'string') {
      const updateFieldsAliases = this.dependency.update.split(',');
      updateFieldsAliases.forEach((updateFieldAlias) => {
        this.eventBus.on(`change:${updateFieldAlias}`, () => {
          // force field type to be destroyed and remounted
          this.$nextTick(() => {
            this.rendered = false;
            this.$nextTick(() => {
              this.rendered = true;
            });
          });
        });
      });
    }
  },
  mounted() {
    // console.log('fields.ctx', this.expressionInterpreter.fieldsContext);
  },
  beforeDestroy() {
    if (this.eventBus != null) {
      this.eventBus.off(`change:${this.alias}`);
    }
  },
};
</script>

<style lang="scss" scoped>
  .required-icon {
    font-size: 0.6rem;
    padding-top: 0.4rem;
  }
  .container-hidden {
    // display: contents;
    display: block;
  }
  .component-field{
    display: flex;
  }
</style>

<style lang="scss">
  .theme--light.v-text-field.v-input {
    margin-top: 6px;
  }
</style>
